import axios, { AxiosRequestConfig as _AxiosRequestConfig, Method } from 'axios'
import { message as Message } from 'antd'
import { has, isEqual, isObject, omit, some, includes } from 'lodash'
import { isMobile } from 'react-device-detect'
import { authStore, globalStore } from '@store/index'
import { isProdEnv, makeDetailMessage, showToast, TOAST_TYPE } from '@utils/utils'
import {
  API_URL,
  DUPLICATE_REQUEST_CHECK_TIME_LIMIT,
  DUPLICATE_REQUEST_CHECK_URL,
  DUPLICATE_REQUEST_EXCEPTION,
  SERVER_STATE,
} from '@constants/index'
import { EXCEPTION_TYPE, LogoutType } from '@interface/common'
import StorageUtils from '@utils/storage-utils'

export interface AxiosRequestConfig extends _AxiosRequestConfig {
  startTime?: Date
}

export interface HttpRequest {
  get?(url: string, data: object, baseUrl?: string, config?: AxiosRequestConfig): Promise<any>

  post?(url: string, data: object, baseUrl?: string, config?: AxiosRequestConfig): Promise<any>

  delete?(url: string, data: object, baseUrl?: string, config?: AxiosRequestConfig): Promise<any>

  put?(url: string, data: object, baseUrl?: string, config?: AxiosRequestConfig): Promise<any>
}

enum HTTP_ERROR {
  LOGIC_ERROR,
  TIMEOUT_ERROR,
  NETWORK_ERROR,
  SERVICE_ERROR,
}

class CustomError extends Error {
  type: HTTP_ERROR

  config: _AxiosRequestConfig

  constructor(message: string, type: HTTP_ERROR, config: _AxiosRequestConfig) {
    super(message)
    this.type = type
    this.config = config
  }
}

const TOKEN_ERROR = [401, 402, 403]

const DEFAULT_CONFIG = {
  baseURL: `${process.env.BACKEND_BASE_URL}:${process.env.BACKEND_PORT}/${process.env.BACKEND_PREFIX}`,
}

const http: HttpRequest = {}
const methods: Method[] = ['get', 'post', 'put', 'delete']

let authTimer: NodeJS.Timer = null

// const isSuccess = res => res.errCode === 0
const isSuccess = res => isObject(res)
// const resFormat = res => res.response || res.data || {}

let beforeRequestInfoMap = {}

const unregistBeforeRequestInfoMap = (url) => {
  if (has(beforeRequestInfoMap, url)) {
    beforeRequestInfoMap = omit(beforeRequestInfoMap, url)
  }
}

methods.forEach(v => {
  http[v] = (url: string, data: object, baseUrl?: string, config?: AxiosRequestConfig) => {
    console.log(new Date().getHours(), new Date().getMinutes(), new Date().getSeconds(), new Date().getMilliseconds(), 'Http request', v, url)
    const validBaseUrl = baseUrl || DEFAULT_CONFIG.baseURL
    const defaultRequestConfig = {
      method: v,
      url,
      baseURL: validBaseUrl,
      // headers: { Authorization: `Bearer ${userInfo.token}` }
      withCredentials: true,
    }
    const axiosConfig: AxiosRequestConfig = {
      ...defaultRequestConfig,
      ...config,
    }

    const instance = axios.create(DEFAULT_CONFIG)
    // Add a request interceptor
    instance.interceptors.request.use(
      cfg => {
        cfg.params = { ...cfg.params, ts: Date.now() / 1000 }
        return cfg
      },
      error => Promise.reject(error),
    )


    instance.interceptors.response.use(
      response => {
        const rdata = response.data

        // RETURN 데이터가 하나도 없을때.
        if (!isSuccess(rdata)) {
          return Promise.reject(new CustomError('data not found!', HTTP_ERROR.LOGIC_ERROR, response.config))
        }

        if (rdata.errcode != null) {
          console.error(`error url : ${url} , errcode : ${rdata.errcode}`)
        }

        // 서비스에러일때 강제적으로 토스트 팝업을 띄운다. Error
        if (rdata.error) {
          if (
            rdata.error === EXCEPTION_TYPE.AUTHENTICATION_FAILURE ||
            rdata.error === EXCEPTION_TYPE.USER_NICK_NAME_NOT_FOUND ||
            rdata.error === EXCEPTION_TYPE.CERTIFICATION_NOT_FOUND ||
            rdata.error === EXCEPTION_TYPE.CERTIFICATION_EXPIRED ||
            rdata.error === EXCEPTION_TYPE.ACCOUNT_DUPLICATED ||
            rdata.error === EXCEPTION_TYPE.SERVER_STATUS
          ) {
            return Promise.resolve(rdata)
          }

          if (rdata.error === EXCEPTION_TYPE.LOGOUT) {
            // 실제 로그아웃을 했을때가 아니라 서버가 운영중이 아니면 처리하는 로직 추가
            clearTimeout(authTimer)
            const clubIdUrlValue = globalStore.getClubIdFromUrl()
            if (clubIdUrlValue) {
              StorageUtils.saveLastClubId(clubIdUrlValue)
            }
            if (isMobile) {
              authTimer = setTimeout(() => {
                window.location.replace('/#/serverCheck')
              }, 300)
            }
            globalStore.setServerState(SERVER_STATE.OFF)
            return Promise.reject(new CustomError(rdata.detail, HTTP_ERROR.SERVICE_ERROR, response.config))
          }

          if (rdata.detail) {
            const detailMessage = makeDetailMessage(rdata.detail)
            return Promise.reject(new CustomError(detailMessage, HTTP_ERROR.SERVICE_ERROR, response.config))
          }
          return Promise.reject(new CustomError(rdata.message, HTTP_ERROR.SERVICE_ERROR, response.config))
        }

        if (!some(
          [API_URL.GAME_GET_GLOBAL_SETTINGS, API_URL.GAME_MARKET_PROVIDER, API_URL.COMPONENT_MAINTENANCE_BANNER],
          url => includes(response.request.responseURL, url))) {
          const beforeServerState = globalStore.serverState
          globalStore.setServerState(SERVER_STATE.ON)

          if (isMobile && beforeServerState === SERVER_STATE.OFF) {
            window.location.replace('/')
          }
        }

        return Promise.resolve(rdata)
      },
      error => {
        if (error.response && TOKEN_ERROR.includes(error.response.status)) {
          Message.destroy()
          // Message.error('Authentication failure, Please relogin!')
          clearTimeout(authTimer)
          authTimer = setTimeout(async () => {
            // 서버에서 중복 로그인일경우 403일경우 주도록 수정
            if ((authStore.isSignedIn && error.response.status === 401) || error.response.status === 403) {
              showToast(TOAST_TYPE.INFO, '중복 로그인으로 로그아웃 합니다.')
              await authStore.logout(LogoutType.DUPLICATE)
            }
            const clubIdUrlValue = globalStore.getClubIdFromUrl()
            if (clubIdUrlValue) {
              StorageUtils.saveLastClubId(clubIdUrlValue)
            }
            // window.location.replace('/')
          }, 300)
          return
        }

        if (error.response && error.response.status === 500) {
          showToast(TOAST_TYPE.ERROR, '서버 연결이 원활하지 않습니다.')
          return
        }

        if(error.response) {
          if (error.response.status === 408 || error.code === 'ECONNABORTED') {
            console.error(`A timeout happend on url ${error.config.url}`)
            return
          }
        }

        let msg = error.response ? error.response.statusText : error.message || 'network error'
        if (msg.toLowerCase() === 'network error' && isProdEnv()) {
          console.error(msg)
          return
        }

        if (error.response && error.response.data) {
          msg = error.response.data.detail || error.response.data.message
        }
        throw new CustomError(
          msg,
          /^timeout of/.test(error.message) ? HTTP_ERROR.TIMEOUT_ERROR : HTTP_ERROR.NETWORK_ERROR,
          error.config,
        )
      },
    )
    if (v === 'get') {
      axiosConfig.params = data
    } else if (data instanceof FormData) {
      axiosConfig.data = data
    } else {
      axiosConfig.data = data
    }
    axiosConfig.startTime = new Date()

    let isDuplicatedRequest = false
    const requestTime = new Date().getTime()

    const info = beforeRequestInfoMap[url]
    if (info) {
      if (requestTime < (info.time + DUPLICATE_REQUEST_CHECK_TIME_LIMIT)
        && isEqual(data, info.data)) {
        isDuplicatedRequest = true
      }
    }

    const isTarget = DUPLICATE_REQUEST_CHECK_URL.find(checkUrl => checkUrl.toLowerCase() === url.toLowerCase())
    if (isTarget) {
      beforeRequestInfoMap[axiosConfig.url] = { data, time: requestTime }
    }

    if (!isDuplicatedRequest) {
      return instance
        .request(axiosConfig)
        .then(res => {
          unregistBeforeRequestInfoMap(url)
          return res
        })
        .catch(err => {
          unregistBeforeRequestInfoMap(url)
          console.error(err.response || err.message || err.stack || 'unknown error')
          return Promise.reject(axiosConfig.url.includes('autoScript.set') ? { err } : err)
        })
    }

    console.log('duplicate request.')
    return Promise.resolve({ error: DUPLICATE_REQUEST_EXCEPTION })
  }
})

export default http
